import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeMode } from '../context/ThemeContext';
import { useLocale } from '../i18n/LocaleContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

/**
 * Light, dark, or the device's. One icon button in the site's chrome; a menu with the three
 * choices. `compact` draws the three choices as a segmented row (the phone menu, the footer).
 */
export default function ThemeToggle({ compact = false, className }) {
  const { mode, resolved, setTheme } = useThemeMode();
  const { t } = useLocale();
  const options = [
    { id: 'light', Icon: Sun, label: t('nav.themeLight') },
    { id: 'dark', Icon: Moon, label: t('nav.themeDark') },
    { id: 'system', Icon: Monitor, label: t('nav.themeSystem') },
  ];

  if (compact) {
    return (
      <div role="group" aria-label={t('nav.theme')} className={cn('inline-flex items-center rounded-full border border-border bg-muted/40 p-1', className)}>
        {options.map(({ id, Icon, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTheme(id)}
            aria-pressed={mode === id}
            title={label}
            className={cn(
              'flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] transition-colors',
              mode === id ? 'bg-background font-semibold text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span className={id === 'system' ? 'sr-only' : ''}>{label}</span>
          </button>
        ))}
      </div>
    );
  }

  const Current = resolved === 'dark' ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('nav.theme')}
          className={cn('flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground', className)}
        >
          <Current className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 rounded-xl p-1.5">
        {options.map(({ id, Icon, label }) => (
          <DropdownMenuItem
            key={id}
            onClick={() => setTheme(id)}
            className={cn('gap-2.5 rounded-lg px-2.5 py-2 cursor-pointer', mode === id && 'bg-muted font-semibold')}
            aria-current={mode === id ? 'true' : undefined}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
